import React from 'react';
import type { Amount, Fee, FeeSection } from 'app/types/costAndFees.type';
import { useCostsAndFeesContext } from 'app/shared/modules/hdp/CostsAndFees/context/CostsAndFeesProvider';
import Card from '../../../ui/Card';
import * as styles from './costAndFeesModule.styles';

type FeeBreakdownCardProps = {
  title: string;
  feeSection: FeeSection;
  totalDisplayName?: string;
  totalAmount?: Amount;
};

const FeeRow = ({ fee }: { fee: Fee }) => {
  const { getDisplayAmount, getSelectedOption, updateSelectedOption } = useCostsAndFeesContext();
  const optional = fee.counter.min === 0;
  const selectedOption = getSelectedOption(fee);
  const isChecked = !!selectedOption;

  const getFeeTotal = () => {
    const count = selectedOption ? selectedOption.counterValue : fee.counter.min;
    const effectiveCount = isChecked || !optional ? count : 0;

    const feeAmount = fee.amount;
    if ('displayText' in feeAmount) {
      return feeAmount.displayText;
    }

    const maxAmount = feeAmount.max ?? feeAmount.min;
    const finalAmount: Amount = {
      min: feeAmount.min * effectiveCount,
      max: maxAmount * effectiveCount,
    };

    return getDisplayAmount(finalAmount);
  };

  const handleCheckboxChange = () => {
    if (!optional) return;

    if (isChecked) {
      updateSelectedOption(fee, null);
    } else {
      updateSelectedOption(fee, {
        counterValue: Math.max(1, fee.counter.min),
        optionSelected: fee.options?.[0]?.displayName,
      });
    }
  };

  return (
    <div className={styles.feeRow}>
      <span className={styles.feeLabel}>
        {optional && (
          <input
            type="checkbox"
            className={styles.checkbox}
            checked={isChecked}
            onChange={handleCheckboxChange}
            aria-label={`Toggle ${fee.displayName}`}
          />
        )}
        <span>{fee.displayName}</span>
      </span>
      <span className={styles.feeAmount}>{getFeeTotal()}</span>
    </div>
  );
};

export const FeeBreakdownCard = ({ title, feeSection, totalDisplayName, totalAmount }: FeeBreakdownCardProps) => {
  const { getDisplayAmount } = useCostsAndFeesContext();

  if (!feeSection?.feeGroup || feeSection.feeGroup.length === 0) return null;

  return (
    <Card variant="default" size="md" title={title} collapsible defaultCollapsed={false}>
      {feeSection.feeGroup.map((group, groupIndex) => (
        <div key={`${group.type}-${groupIndex}`}>
          {group.displayName && <div className={styles.feeGroupTitle}>{group.displayName}</div>}
          {group.fees?.map((fee) => (
            <FeeRow key={fee.displayName} fee={fee} />
          ))}
        </div>
      ))}
      {totalDisplayName && totalAmount && (
        <div className={styles.estimatedTotal}>
          <span>{totalDisplayName}</span>
          <span>{getDisplayAmount(totalAmount)}</span>
        </div>
      )}
    </Card>
  );
};
